import { callFunction } from "./functions-client";

// Typed calls to the `loads` edge function group (the freight side). The load
// board is a uShip-comparable shipment marketplace: categorized listings with
// dimensions, distance, and flexible dates, plus reverse-auction bidding.

// Broker/dispatcher and company (carrier) profiles post loads.
export const LOAD_POST_TYPES = ["broker_dispatcher", "company"];
// Company (carrier) and driver (owner-operator) profiles bid to haul.
export const LOAD_BID_TYPES = ["company", "driver"];

// Option lists — kept in sync with the server-side enums in loads/logic.ts.
export const CATEGORY_OPTIONS: [string, string][] = [
  ["freight", "Freight (FTL)"],
  ["ltl", "Less-than-truckload (LTL)"],
  ["vehicle", "Vehicle"],
  ["heavy_equipment", "Heavy equipment"],
  ["household", "Household goods"],
  ["boat", "Boat"],
  ["motorcycle", "Motorcycle"],
  ["other", "Other"],
];
export const EQUIPMENT_TYPE_OPTIONS: [string, string][] = [
  ["dry_van", "Dry van"],
  ["reefer", "Reefer"],
  ["flatbed", "Flatbed"],
  ["step_deck", "Step deck"],
  ["power_only", "Power only"],
  ["box_truck", "Box truck"],
  ["tanker", "Tanker"],
  ["hotshot", "Hotshot"],
  ["conestoga", "Conestoga"],
  ["lowboy", "Lowboy / RGN"],
  ["car_hauler", "Car hauler"],
  ["other", "Other"],
];
export const FULL_PARTIAL_OPTIONS: [string, string][] = [
  ["full", "Full load"],
  ["partial", "Partial"],
];
export const RATE_TYPE_OPTIONS: [string, string][] = [
  ["flat", "Flat rate ($)"],
  ["per_mile", "Per mile (¢/mi)"],
  ["negotiable", "Negotiable"],
];
export const SPECIAL_REQUIREMENT_OPTIONS = [
  "hazmat", "tarps", "straps", "team", "twic", "liftgate", "temp_controlled", "oversize", "chains", "load_bars",
];

export interface LoadSummary {
  id: string;
  origin: string;
  destination: string;
  category: string | null;
  equipment_type: string | null;
  full_partial: string | null;
  commodity: string | null;
  weight: number | null;
  trip_miles: number | null;
  pickup_date: string | null;
  delivery_date: string | null;
  rate: number | null;
  rate_type: string;
  created_at: string;
  poster_name: string | null;
  poster_type: string;
  origin_distance_mi?: number | null;
}

export interface LoadDetail extends LoadSummary {
  length_ft: number | null;
  width_ft: number | null;
  height_ft: number | null;
  pieces: number | null;
  temperature: string | null;
  pickup_time: string | null;
  delivery_time: string | null;
  flexible_dates: boolean;
  reference_number: string | null;
  special_requirements: string[];
  description: string | null;
  status: string;
  poster_profile_id: string;
}

export interface MyLoad {
  id: string;
  origin: string;
  destination: string;
  category: string | null;
  equipment_type: string | null;
  full_partial: string | null;
  commodity: string | null;
  weight: number | null;
  trip_miles: number | null;
  pickup_date: string | null;
  delivery_date: string | null;
  rate: number | null;
  rate_type: string;
  status: string;
  created_at: string;
  bid_count: number;
}

export interface LoadBid {
  id: string;
  amount: number;
  note: string | null;
  status: string;
  created_at: string;
  bidder_profile_id?: string;
  display_name?: string | null;
  headline?: string | null;
  load_id?: string;
  origin?: string;
  destination?: string;
  load_status?: string;
  poster_name?: string | null;
}

export interface CreateLoadInput {
  poster_profile_id: string;
  org_id?: string | null;
  origin: string;
  destination: string;
  category?: string;
  equipment_type?: string;
  full_partial?: string;
  commodity?: string;
  weight?: number;
  length_ft?: number;
  width_ft?: number;
  height_ft?: number;
  pieces?: number;
  trip_miles?: number;
  temperature?: string;
  pickup_date?: string;
  pickup_time?: string;
  delivery_date?: string;
  delivery_time?: string;
  flexible_dates?: boolean;
  rate?: number;
  rate_type?: string;
  reference_number?: string;
  special_requirements?: string[];
  description?: string;
}

function qs(params: Record<string, string | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v);
  return entries.length ? "?" + new URLSearchParams(entries as [string, string][]).toString() : "";
}

export const listLoads = (
  { origin_radius, ...rest }: { q?: string; origin?: string; destination?: string; equipment?: string; category?: string; full_partial?: string; origin_radius?: number } = {},
) => callFunction<{ loads: LoadSummary[] }>(`loads/list${qs({ ...rest, origin_radius: origin_radius != null ? String(origin_radius) : undefined })}`, { method: "GET" });

export const getLoad = (id: string) =>
  callFunction<{ load: LoadDetail }>(`loads/get${qs({ id })}`, { method: "GET" });

export const createLoad = (input: CreateLoadInput) =>
  callFunction<{ load: { id: string } }>("loads/create", { body: input });

export const myLoads = (profileId: string) =>
  callFunction<{ loads: MyLoad[] }>(`loads/mine${qs({ profile_id: profileId })}`, { method: "GET" });

export const setLoadStatus = (id: string, status: "open" | "closed") =>
  callFunction<{ load: { id: string; status: string } }>("loads/close", { body: { id, status } });

export const bidOnLoad = (load_id: string, bidder_profile_id: string, amount: number, note?: string) =>
  callFunction<{ bid: { id: string; status: string } }>("loads/bid", {
    body: { load_id, bidder_profile_id, amount, note },
  });

export const loadBids = (loadId: string) =>
  callFunction<{ bids: LoadBid[] }>(`loads/bids${qs({ load_id: loadId })}`, { method: "GET" });

export const myBids = (profileId: string) =>
  callFunction<{ bids: LoadBid[] }>(`loads/my-bids${qs({ profile_id: profileId })}`, { method: "GET" });

export const awardBid = (bid_id: string) =>
  callFunction<{ ok: boolean }>("loads/award", { body: { bid_id } });

export const declineBid = (bid_id: string) =>
  callFunction<{ ok: boolean }>("loads/decline", { body: { bid_id } });

export const withdrawBid = (bid_id: string) =>
  callFunction<{ ok: boolean }>("loads/withdraw", { body: { bid_id } });

/** "$2,400" or "$2.10/mi" or "Negotiable" rate label. */
export function rateLabel(rate: number | null, rateType: string): string {
  if (rateType === "negotiable" || rate == null) return "Negotiable";
  if (rateType === "per_mile") return `$${(rate / 100).toFixed(2)}/mi`;
  return `$${rate.toLocaleString()}`;
}

/** Effective $/mile: given directly (per_mile) or derived from a flat rate over
 *  the trip distance. Null when it can't be computed. */
export function ratePerMile(rate: number | null, rateType: string, tripMiles: number | null): string | null {
  if (rateType === "per_mile" && rate != null) return `$${(rate / 100).toFixed(2)}/mi`;
  if (rateType === "flat" && rate != null && tripMiles && tripMiles > 0) return `$${(rate / tripMiles).toFixed(2)}/mi`;
  return null;
}

/** "48 × 8 × 8 ft" dimensions label, or null when none set. */
export function dimsLabel(l: number | null, w: number | null, h: number | null): string | null {
  if (!l && !w && !h) return null;
  return `${l ?? "—"} × ${w ?? "—"} × ${h ?? "—"} ft`;
}

/** "Dallas, TX → Denver, CO" lane label. */
export function laneLabel(origin: string, destination: string): string {
  return `${origin} → ${destination}`;
}
